import React, { useState, useEffect, useRef } from 'react';
import { AuthRequest } from '../../types/auth';
import { authService } from '../../services/authService';
import { presetUsers } from '../../data/presetUsers';
import { useUser } from '../../contexts/UserContext';

import Button from '../../components/Button/Button';
import Step from '../../components/Step/Step';
import LoaderPanel from '../../components/LoaderPanel/LoaderPanel';
import Template from '../../components/Template/Template';
import CtaWrapper from '../../components/CtaWrapper/CtaWrapper';

import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';

import styles from './index.module.scss';

interface RegisterProps {
  changeStep: (step: 
    // 'terms-of-use' |
    'register' |
    'home'
  ) => void;
}

// Funzione per generare dati casuali
const generateRandomData = (): AuthRequest => {
  const randomId = Math.floor(Math.random() * 900000000) + 100000000; // 9 cifre (100000000-999999999)
  const randomToken = Math.random().toString(36).substring(2, 15);
  const randomNames = ['Marco', 'Giulia', 'Luca', 'Sofia', 'Andrea', 'Chiara', 'Roberto', 'Elena', 'Paolo', 'Anna'];
  const randomSurnames = ['Verdi', 'Neri', 'Gialli', 'Blu', 'Viola', 'Arancioni', 'Rosa', 'Grigi', 'Marroni', 'Bianchi'];

  const randomName = randomNames[Math.floor(Math.random() * randomNames.length)];
  const randomSurname = randomSurnames[Math.floor(Math.random() * randomSurnames.length)];
  
  return {
    mcdonaldsId: randomId.toString(),
    deviceId: `DEV_RND_${randomId}`,
    deviceToken: `TOKEN_${randomToken.toUpperCase()}`,
    email: `${randomName.toLowerCase()}.${randomSurname.toLowerCase()}@mcdonalds.com`,
    firstname: randomName,
    lastname: randomSurname,
    nickname: `MADONNA`
  };
};

const RegistrationForm: React.FC<RegisterProps> = ({ changeStep }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const [showPresetUsers, setShowPresetUsers] = useState(false);
  
  const { setUser, gmalUser } = useUser();
  const [formData, setFormData] = useState<AuthRequest>({
    mcdonaldsId: '',
    deviceId: '',
    deviceToken: '',
    email: '',
    firstname: '',
    lastname: '',
    nickname: '',
    acceptTerms: false,
  });

  const [preFormValid, setPreFormValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Stati per l'autocomplete dei nickname
  const [allNames, setAllNames] = useState<string[]>([]);
  const [filteredNames, setFilteredNames] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Funzione per validare il form in tempo reale
  const isFormValid = (): boolean => {

    return !!(
      formData.mcdonaldsId.trim() &&
      formData.deviceId.trim() &&
      formData.deviceToken.trim() &&
      formData.email.trim() &&
      /\S+@\S+\.\S+/.test(formData.email) &&
      formData.firstname.trim() &&
      formData.lastname.trim() &&
      formData.nickname?.trim() &&
      formData.nickname == allNames.find(name => name === formData.nickname)
      && formData.acceptTerms
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const loadPresetUser = (user: typeof presetUsers[0]) => {
    let userData: AuthRequest;
    
    if (user.id === 'random') {
      // Genera nuovi dati casuali per l'utente Random
      userData = generateRandomData();
    } else {
      userData = user.data;
    }
    
    // Precompila tutti i campi inclusi acceptTerms
    setFormData({
      ...userData,
      acceptTerms: true, // Precompila il checkbox per i preset
    });

    handleNext();
    setError(null);
  };

  const clearForm = () => {
    setFormData({
      mcdonaldsId: '',
      deviceId: '',
      deviceToken: '',
      email: '',
      firstname: '',
      lastname: '',
      nickname: '',
      acceptTerms: false, // Resetta il checkbox quando si pulisce
    });
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.mcdonaldsId.trim()) {
      setError('Il McDonalds ID è obbligatorio');
      return false;
    }
    if (!formData.deviceId.trim()) {
      setError('Il Device ID è obbligatorio');
      return false;
    }
    if (!formData.deviceToken.trim()) {
      setError('Il Device Token è obbligatorio');
      return false;
    }
    if (!formData.email.trim()) {
      setError('L\'email è obbligatoria');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Inserisci un\'email valida');
      return false;
    }
    if (!formData.firstname.trim()) {
      setError('Il nome è obbligatorio');
      return false;
    }
    if (!formData.lastname.trim()) {
      setError('Il cognome è obbligatorio');
      return false;
    }
    if (formData.nickname && !formData.nickname?.trim()) {
      setError('Il nickname è obbligatorio');
      return false;
    }
    // if (!formData.acceptTerms) {
    //   setError('Devi accettare i termini e le condizioni');
    //   return false;
    // }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Crea un oggetto per l'API escludendo acceptTerms
      const apiData = {
        mcdonaldsId: formData.mcdonaldsId,
        deviceId: formData.deviceId,
        deviceToken: formData.deviceToken,
        email: formData.email,
        firstname: formData.firstname,
        lastname: formData.lastname,
        nickname: formData.nickname
      };

      const response = await authService.register(apiData);
      if (response.token) {
        console.log(response.token);
        authService.saveToken(response.token);
        
        // Aggiorna i dati utente globali con i dati del form (escludendo acceptTerms)
        setUser({
          plays: [],
          mcdonaldsId: formData.mcdonaldsId,
          deviceId: formData.deviceId,
          deviceToken: formData.deviceToken,
          email: formData.email,
          firstname: formData.firstname,
          lastname: formData.lastname,
          nickname: formData.nickname || '',
        });
        
        changeStep('home');
      } else {
        setError('Registrazione completata ma JWT non ricevuto');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      // Avanza il carosello del tutorial
      setShowPresetUsers(false);
      swiperRef.current.slideNext();
    }
  };
  const handlePrev = () => {
    if (swiperRef.current) {
      // Avanza il carosello del tutorial
      swiperRef.current.slidePrev();
    }
  };

  const handleButtonClick = () => {
    // Crea un evento fittizio per mantenere la compatibilità con handleSubmit
    const fakeEvent = {
      preventDefault: () => {}
    } as React.FormEvent;
    if (isFormValid()) {
      handleSubmit(fakeEvent);
    } else {
      handlePrev();
    }
  };

  useEffect(() => {
    if (gmalUser) {
      setFormData(gmalUser);
    }
  }, [gmalUser]);

  // Carica i nomi dalla URL
  useEffect(() => {
    const fetchNames = async () => {
      try {
        const response = await fetch('https://friends.c1-p8.com/nomi_sorted.json');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const names = await response.json();
        setAllNames(names);
        console.log(`Caricati ${names.length} nomi con successo`);
      } catch (error) {
        console.error('Errore nel caricamento dei nomi:', error);
        // Fallback: usa un array vuoto o mostra un messaggio all'utente
        setError('Impossibile caricare la lista dei nickname. Riprova più tardi.');
      }
    };
    setTimeout(() => {
      setLoading(false);
    }, 450);
    fetchNames();
  }, []);

  // Gestisce il cambio del nickname e filtra i suggerimenti
  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase(); // Converte in maiuscolo
    setFormData(prev => ({ ...prev, nickname: value }));

    if (value.length > 0) {
      const filtered = allNames.filter(name => 
        name.startsWith(value)
      );
      setFilteredNames(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredNames([]);
      setShowSuggestions(false);
    }
  };

  // Seleziona un nickname dalla lista
  const selectNickname = (name: string) => {
    setFormData(prev => ({ ...prev, nickname: name }));
    setShowSuggestions(false);
    setFilteredNames([]);
  };

  // Chiudi i suggerimenti quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(`.${styles.autocompleteWrapper}`)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSlideChange = (swiper: SwiperType) => {
    setCurrentStep(swiper.activeIndex);
  };

  const testUser = () => {
    setShowPresetUsers(true);
  };

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.checked
    }));
    setPreFormValid(e.target.checked);
  };

  const restartTutorial = () => {
    localStorage.setItem('tutorialCompleted', '');
    window.location.reload();
  };


  const handlePrivacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrivacyAccepted(e.target.checked);
  };
  return (
    <Step>
      <LoaderPanel active={loading} />
      <div className={styles.registerPage}>
        <Swiper
          spaceBetween={0}
          slidesPerView={1}
          allowTouchMove={false}
          navigation={false}
          pagination={false}
          autoplay={false}
          onSwiper={(swiper: SwiperType) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={handleSlideChange}
          >
          <SwiperSlide>
            <Template title="Registrazione" legalLine="simple">
              <div className={styles.formContent}>
                <h3 className='f-regular'>
                  Vuoi partecipare al Friends Quiz?<br />
                  Registrati qui!
                </h3>
                {showPresetUsers && (
                  <section className={styles.presetUsers}>
                    <h3>Seleziona un utente predefinito:</h3>
                    <div className={styles.presetButtons}>
                      {presetUsers.map((user) => (
                        <Button
                          key={user.id}
                          color='black'
                          onClick={() => loadPresetUser(user)}
                        >
                          {user.name}
                        </Button>
                      ))}
                      <button
                        className={styles.clearButton}
                        onClick={clearForm}
                      >
                        Pulisci i campi
                      </button>
                    </div>
                  </section>
                )}
                <form onSubmit={handleSubmit} className={styles.registerForm}>
                  
                  {error && (
                    <div className={styles.errorMessage}>
                      {error}
                    </div>
                  )}

                  <div className={styles.formRow} style={{ display: showPresetUsers ? 'block' : 'none' }}>
                    <input
                      type="text"
                      id="mcdonaldsId"
                      name="mcdonaldsId"
                      value={formData.mcdonaldsId}
                      onChange={handleInputChange}
                      placeholder="McDonalds ID"
                      required
                      readOnly
                    />
                  </div>

                  <div className={styles.formRow} style={{ display: showPresetUsers ? 'block' : 'none' }}>
                    <input
                      type="text"
                      id="deviceId"
                      name="deviceId"
                      value={formData.deviceId}
                      onChange={handleInputChange}
                      placeholder=" Device ID"
                      required
                      readOnly
                    />
                  </div>

                  <div className={styles.formRow} style={{ display: showPresetUsers ? 'block' : 'none' }}>
                    <input
                      type="text"
                      id="deviceToken"
                      name="deviceToken"
                      value={formData.deviceToken}
                      onChange={handleInputChange}
                      placeholder="Device Token"
                      required
                      readOnly
                    />
                  </div>

                  <div className={styles.formRow} style={{ display: showPresetUsers ? 'block' : 'none' }}>
                    <input
                      type="text"
                      id="firstname"
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleInputChange}
                      placeholder="Nome"
                      required
                      readOnly
                    />
                  </div>

                  <div className={styles.formRow} style={{ display: showPresetUsers ? 'block' : 'none' }}>
                    <input
                      type="text"
                      id="lastname"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleInputChange}
                      placeholder="Cognome"
                      required
                      readOnly
                    />
                  </div>

                  <div className={styles.formRow} style={{ display: showPresetUsers ? 'block' : 'none' }}>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email"
                      required
                      readOnly
                    />
                  </div>

                  <div className={styles.formRow}>
                    <label htmlFor="nickname">Inserisci il tuo nome</label>
                    <div className={styles.autocompleteWrapper}>
                      <input
                        type="text"
                        id="nickname"
                        name="nickname"
                        value={formData.nickname || ''}
                        onChange={handleNicknameChange}
                        onFocus={() => setShowSuggestions(true)}
                        placeholder="nome"
                        autoComplete="off"
                        style={{ textAlign: 'center' }}
                      />
                      {showSuggestions && filteredNames.length > 0 && (
                        <ul className={styles.suggestionsList}>
                          {filteredNames.slice(0, 10).map((name, index) => (
                            <li 
                              key={index}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectNickname(name);
                              }}
                              onTouchStart={(e) => {
                                e.preventDefault();
                                selectNickname(name);
                              }}
                              className={styles.suggestionItem}
                            >
                              {name}
                            </li>
                          ))}
                          {filteredNames.length > 10 && (
                            <li className={styles.suggestionMore}>
                              ... e altri {filteredNames.length - 10} risultati
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <input type="checkbox" id="acceptTerms" name="acceptTerms" checked={formData.acceptTerms} onChange={handleConsentChange} />
                    <label htmlFor="acceptTerms">
                      Dichiaro di avere almeno 16 anni<br />
                      e di aver letto il <a href="https://www.mcdonalds.it/friends-quiz--regolamento?openOutsideMcd=true" rel="noreferrer">regolamento del concorso</a>.
                    </label>
                  </div>
                  
                  <p className={styles.SmallLegal}>
                    Nota bene: se dominerai il FriendsQuiz in Italia, il tuo nome apparirà sugli schermi nei McDonald's aderenti.
                  </p>

                </form>
              </div>
              <footer>
                <CtaWrapper full>
                  {process.env.NODE_ENV === 'development' && (
                    <Button onClick={() => testUser()}>Seleziona un utente predefinito</Button>
                  )}
                  <Button disabled={!isFormValid()} onClick={() => handleNext()}>Avanti</Button>
                </CtaWrapper>
              </footer>
            </Template>
          </SwiperSlide>
          <SwiperSlide>
            <Template title="Registrazione" legalLine="simple">
              <div className={styles.formContent}>
                <h3 className='boxed'>
                  Cliccando su "Procedi" autorizzi a conferire i tuoi dati presenti in App a Consorzio McCoop.<br />Precisiamo che il conferimento dei tuoi dati è necessario per poter partecipare al concorso.
                </h3>
                <div className={styles.formGroup}>
                  <input type="checkbox" id="consentPrivacy" name="consentPrivacy" onChange={handlePrivacyChange} />
                  <label htmlFor="consentPrivacy">Presa visione dell&apos;<a href="https://www.mcdonalds.it/friends-quiz--privacy-policy?openOutsideMcd=true" rel="noreferrer">Informativa Privacy</a></label>
                </div>
                <p className={styles.SmallLegalDisclaimer}>
                  Per partecipare devi essere registrato all'iniziativa, attivare la geolocalizzazione (e averne dato il consenso) e partecipare a un'intera sessione di Quiz.
                </p>
              </div>
              <footer>
                <CtaWrapper full>
                  <Button disabled={!privacyAccepted} onClick={handleButtonClick}>Procedi</Button>
                </CtaWrapper>
              </footer>
            </Template>
          </SwiperSlide>
        </Swiper>
      </div>
    </Step>
  );
};

export default RegistrationForm; 